import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  LifeBuoy, Plus, Send, ChevronDown, MessageSquare, Building2, CircleDot,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import api, { formatApiErrorDetail } from "@/lib/api";

export const STATUS_META = {
  open: { label: "Open", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  in_progress: { label: "In Progress", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  resolved: { label: "Resolved", cls: "bg-green-100 text-green-800 border-green-200" },
  closed: { label: "Closed", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export const PRIORITY_META = {
  low: { label: "Low", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  normal: { label: "Normal", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  high: { label: "High", cls: "bg-red-100 text-red-800 border-red-200" },
};

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function TicketsPanel({ isAdmin = false, onChange }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", priority: "normal" });
  const [busy, setBusy] = useState(false);
  // detail
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null); // {ticket, messages}
  const [reply, setReply] = useState("");
  const threadEndRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(isAdmin ? "/admin/tickets" : "/tickets");
      setTickets(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load tickets");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setActiveId(id);
    setDetailOpen(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load ticket");
    }
  };

  const refreshDetail = async (id) => {
    const { data } = await api.get(`/tickets/${id}`);
    setDetail(data);
  };

  useEffect(() => {
    if (detail && threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [detail]);

  const createTicket = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error("Please add a subject and message.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/tickets", form);
      toast.success("Support ticket submitted. We'll be in touch soon!");
      setCreateOpen(false);
      setForm({ subject: "", message: "", priority: "normal" });
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit ticket");
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeId) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${activeId}/messages`, { body: reply.trim() });
      setReply("");
      await refreshDetail(activeId);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reply");
    } finally {
      setBusy(false);
    }
  };

  const updateTicket = async (patch) => {
    if (!activeId) return;
    try {
      await api.patch(`/admin/tickets/${activeId}`, patch);
      toast.success("Ticket updated");
      await refreshDetail(activeId);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update ticket");
    }
  };

  if (loading) {
    return (
      <div className="py-16 grid place-items-center" data-testid="tickets-loading">
        <div className="h-8 w-8 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
      </div>
    );
  }

  const t = detail?.ticket;

  return (
    <div data-testid="tickets-panel">
      {!isAdmin && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">Need a hand? Open a ticket and our team will help.</p>
          <Button onClick={() => setCreateOpen(true)} data-testid="new-ticket-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white">
            <Plus className="h-4 w-4 mr-2" /> New Ticket
          </Button>
        </div>
      )}

      {tickets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center" data-testid="tickets-empty">
          <LifeBuoy className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="mt-4 text-slate-500 max-w-sm mx-auto">
            {isAdmin ? "No support tickets yet. Client requests will appear here." : "You have no support tickets yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="tickets-list">
          {tickets.map((tk) => {
            const sm = STATUS_META[tk.status] || STATUS_META.open;
            const pm = PRIORITY_META[tk.priority] || PRIORITY_META.normal;
            return (
              <Card key={tk.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`ticket-row-${tk.id}`}>
                <CardContent className="p-5">
                  <button onClick={() => openDetail(tk.id)} data-testid={`ticket-open-${tk.id}`} className="w-full text-left">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900 truncate">{tk.subject}</h3>
                          <Badge variant="outline" className={`${sm.cls} border text-xs`}>{sm.label}</Badge>
                          <Badge variant="outline" className={`${pm.cls} border text-xs`}>{pm.label}</Badge>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
                          {isAdmin && tk.church_name && (
                            <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{tk.church_name}</span>
                          )}
                          <span className="inline-flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" />{tk.created_by_name}</span>
                          <span>Updated {fmtDateTime(tk.last_message_at)}</span>
                        </div>
                      </div>
                      <span className="text-xs text-amber-700 font-medium inline-flex items-center gap-1">Open <ChevronDown className="h-3.5 w-3.5 -rotate-90" /></span>
                    </div>
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create ticket dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-ticket-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Open a Support Ticket</DialogTitle>
            <DialogDescription>Tell us what you need help with and we'll respond by email and here.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Subject</Label>
              <Input className="mt-1.5" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} data-testid="ticket-subject-input" placeholder="e.g. My hero image won't update" />
            </div>
            <div>
              <Label>Priority</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2" data-testid="ticket-priority-picker">
                {Object.keys(PRIORITY_META).map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`ticket-priority-${p}`}
                    onClick={() => setForm((f) => ({ ...f, priority: p }))}
                    className={`rounded-lg border p-2.5 text-sm font-medium transition-colors duration-200 ${form.priority === p ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500 text-amber-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                  >
                    {PRIORITY_META[p].label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea className="mt-1.5" rows={5} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} data-testid="ticket-message-input" placeholder="Describe the issue or question..." />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-md" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createTicket} disabled={busy} data-testid="submit-ticket-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white">
              <Send className="h-4 w-4 mr-2" /> Submit Ticket
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ticket-detail-dialog">
          {!detail ? (
            <div className="py-16 grid place-items-center">
              <DialogTitle className="sr-only">Loading ticket</DialogTitle>
              <DialogDescription className="sr-only">Please wait.</DialogDescription>
              <div className="h-8 w-8 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl pr-8">{t.subject}</DialogTitle>
                <DialogDescription className="sr-only">Support ticket conversation thread.</DialogDescription>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Badge variant="outline" className={`${(STATUS_META[t.status] || STATUS_META.open).cls} border text-xs`} data-testid="detail-status-badge">{(STATUS_META[t.status] || STATUS_META.open).label}</Badge>
                  <Badge variant="outline" className={`${(PRIORITY_META[t.priority] || PRIORITY_META.normal).cls} border text-xs`}>{(PRIORITY_META[t.priority] || PRIORITY_META.normal).label} priority</Badge>
                  {isAdmin && t.church_name && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{t.church_name}</span>}
                </div>
              </DialogHeader>

              {isAdmin && (
                <div className="flex flex-wrap gap-2" data-testid="admin-ticket-controls">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid="ticket-status-trigger" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors duration-200">
                        <CircleDot className="h-3.5 w-3.5" /> Status <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuLabel>Set status</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {Object.keys(STATUS_META).map((k) => (
                        <DropdownMenuItem key={k} data-testid={`ticket-set-status-${k}`} onClick={() => updateTicket({ status: k })}>{STATUS_META[k].label}</DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid="ticket-priority-trigger" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors duration-200">
                        Priority <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuLabel>Set priority</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {Object.keys(PRIORITY_META).map((k) => (
                        <DropdownMenuItem key={k} data-testid={`ticket-set-priority-${k}`} onClick={() => updateTicket({ priority: k })}>{PRIORITY_META[k].label}</DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}

              {/* Thread */}
              <div className="mt-2 space-y-3" data-testid="ticket-thread">
                {detail.messages.map((m) => {
                  const fromAdmin = m.sender_role === "admin";
                  return (
                    <div key={m.id} className={`flex ${fromAdmin ? "justify-start" : "justify-end"}`} data-testid={`ticket-msg-${m.id}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${fromAdmin ? "bg-slate-100 text-slate-800" : "bg-amber-600 text-white"}`}>
                        <div className={`text-xs font-semibold mb-0.5 ${fromAdmin ? "text-slate-500" : "text-amber-50"}`}>
                          {fromAdmin ? "TN Web Design" : m.sender_name}
                        </div>
                        <div className="text-sm whitespace-pre-line leading-relaxed">{m.body}</div>
                        <div className={`text-[11px] mt-1 ${fromAdmin ? "text-slate-400" : "text-amber-100"}`}>{fmtDateTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={threadEndRef} />
              </div>

              {/* Reply box */}
              {t.status === "closed" ? (
                <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-500 text-center" data-testid="ticket-closed-note">
                  This ticket is closed.{isAdmin ? " Re-open it via Status to continue." : " Open a new ticket if you need more help."}
                </div>
              ) : (
                <div className="mt-3 flex items-end gap-2">
                  <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="ticket-reply-input" placeholder="Write a reply..." className="flex-1" />
                  <Button onClick={sendReply} disabled={busy || !reply.trim()} data-testid="ticket-reply-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white shrink-0">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
